import { Controller } from 'stimulus';
import Cookies from 'js-cookie';
import events from 'lib/events';

const gaEventsListOnce = [
  events.download_btn.initial_click,
  events.download_btn.match_eezycore_free_dupe,
  events.errors.error404_page,
  events.locale_prompt.locale_prompt_accepted,
  events.locale_prompt.locale_prompt_connects,
  events.locale_prompt.locale_prompt_dismissed,
  events.locale_prompt.switch_language,
  events.promos.promo_popup.popped,
  events.promos.promo_popup.dismissed,
  events.promos.promo_popup.cta_click,
  events.search.click,
  events.search.search_type,
  events.search.without_results,
  events.search.trending_search_click,
  events.sessions.new_account_created,
  events.show_page.download_ad_modal_connects,
  events.show_page.download_limit_modal_connects,
  events.show_page.license_selector_free_modal_closes,
  events.show_page.license_selector_free_modal_connects,
  events.show_page.license_selector_free_modal_free_button,
  events.show_page.license_selector_free_modal_learn_more,
  events.show_page.license_selector_free_modal_pro_button,
  events.site_header.header_join_pro,
  events.site_header.header_login,
  events.site_header.header_signup,
  events.sponsored_search.position,
  events.sponsored_search.without_results,
  events.checkout.error,
  events.sstk.pagination_popup,
  events.sstk.search_popup,
];

const gaEventsListAlways = [
  events.search.filter,
  events.show_page.attribution_link_copied,
  events.site_header.related_tag_click,
  events.sstk.results_click,
];

export default class GoogleAnalyticsController extends Controller {
  connect() {
    this.sentPageView = false;

    window.dataLayer = window.dataLayer || [];
    this.loadGA(window, document, 'script', this.scriptUrl, 'ga');
    this.ga('create', this.trackingId, this.site);

    this.ga((tracker) => {
      Cookies.set('ga-client-id', tracker.get('clientId'));
    });

    if (document.body.getAttribute('data-env') === 'development') {
      this.ga('set', 'checkStorageTask', null); // Don't abort tracking if cookies disabled -- for testing
    }

    this.ga('require', 'displayfeatures');
    this.ga('set', 'anonymizeIp', true);
    this.ga('set', 'dimension4', this.userDimension);
    this.ga('set', 'dimension6', 'apollo');
    this.ga('set', 'transport', 'beacon');

    if (!this.sentPageView && !this.isTurbolinksPreview) {
      this.sentPageView = true;
      this.trackPageView(window.location.href);
    }

    this.trackNoIndex();
    this.removeListeners(gaEventsListOnce, { once: true });
    this.removeListeners(gaEventsListAlways, { once: false });
    this.registerListeners(gaEventsListOnce, { once: true });
    this.registerListeners(gaEventsListAlways, { once: false });
  }

  disconnect() {
    this.removeListeners(gaEventsListOnce, { once: true });
    this.removeListeners(gaEventsListAlways, { once: false });
  }

  registerListeners(eventsList, options) {
    window.addEventListener(events.generic.track_generic_event, this.trackEvent, { once: false });

    eventsList.forEach((eventName) => {
      window.addEventListener(eventName, this.transformEvent, options);
    });
  }

  removeListeners(eventsList, options) {
    window.removeEventListener(events.generic.track_generic_event, this.trackEvent, { once: false });

    eventsList.forEach((eventName) => {
      window.removeEventListener(eventName, this.transformEvent, options);
    });
  }

  transformEvent = (event) => {
    const transformedEvent = event;

    switch (event.type) {
      case events.download_btn.initial_click:
        transformedEvent.detail.gaEventCategory = 'download';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsProToPremium(event.detail.resourceType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        transformedEvent.detail.gaNonInteraction = false;
        break;

      case events.download_btn.match_eezycore_free_dupe:
        transformedEvent.detail.gaEventCategory = 'download';
        transformedEvent.detail.gaEventAction = 'free';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        transformedEvent.detail.gaNonInteraction = false;
        break;

      case events.errors.error404_page:
        transformedEvent.detail.gaEventCategory = '404_page';
        transformedEvent.detail.gaEventAction = window.location.pathname;
        break;

      case events.locale_prompt.locale_prompt_accepted:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = `visit_in_${event.detail.recommendedLocale}`;
        break;

      case events.locale_prompt.locale_prompt_connects:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.locale_prompt.locale_prompt_dismissed:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = 'no_thanks_stay_here';
        break;

      case events.locale_prompt.switch_language:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = `view_diff_lang_${event.detail.toLang}`;
        break;

      case events.promos.promo_popup.cta_click:
        transformedEvent.detail.gaEventCategory = 'pop_up_triggered';
        transformedEvent.detail.gaEventAction = 'premium_fly_in';
        transformedEvent.detail.gaEventLabel = 'see_this_deal';
        break;

      case events.promos.promo_popup.dismissed:
        transformedEvent.detail.gaEventCategory = 'pop_up_triggered';
        transformedEvent.detail.gaEventAction = 'premium_fly_in';
        transformedEvent.detail.gaEventLabel = 'x_dismiss';
        break;

      case events.promos.promo_popup.popped:
        transformedEvent.detail.gaEventCategory = 'pop_up_triggered';
        transformedEvent.detail.gaEventAction = 'premium_fly_in';
        transformedEvent.detail.gaEventLabel = 'popped';
        break;

      case events.search.click:
        transformedEvent.detail.gaEventCategory = 'search-click';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        break;

      case events.search.filter:
        transformedEvent.detail.gaEventCategory = 'search_filters';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsSearchFilterAction(event.detail.filter);
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        transformedEvent.detail.gaEventValue = event.detail.filterValue;
        break;

      case events.search.search_type:
        transformedEvent.detail.gaEventCategory = 'Search';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsSearchType(event.detail.searchType);
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        break;

      case events.search.without_results:
        transformedEvent.detail.gaEventCategory = 'search_without_results';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        break;

      case events.search.trending_search_click:
        transformedEvent.detail.gaEventCategory = 'search_trending_search_click';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.sessions.new_account_created:
        transformedEvent.detail.gaEventCategory = 'account';
        transformedEvent.detail.gaEventAction = 'new account created';
        transformedEvent.detail.gaEventLabel = this.transformAnalyticsProToPremium(event.detail.accountType);
        break;

      case events.show_page.attribution_link_copied:
        transformedEvent.detail.gaEventCategory = 'attribution';
        transformedEvent.detail.gaEventAction = 'copy_attribution';
        break;

      case events.show_page.download_ad_modal_connects:
        transformedEvent.detail.gaEventCategory = 'Show_640x480_DnldPopup';
        transformedEvent.detail.gaEventAction = 'Show_640x480_DnldPopup';
        break;

      case events.show_page.download_limit_modal_connects:
        transformedEvent.detail.gaEventCategory = 'pop_up triggered';
        transformedEvent.detail.gaEventAction = '3 downloads';
        break;

      case events.show_page.license_selector_free_modal_closes:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'user_close';
        break;

      case events.show_page.license_selector_free_modal_connects:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'pop_up';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_free_button:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsLicenseType(event.detail.licenseType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_learn_more:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'premium_license_learn_more';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_pro_button:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsLicenseType(event.detail.licenseType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.header_join_pro:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'go_premium';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.header_login:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'login';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.header_signup:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'join_eezy';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.related_tag_click:
        transformedEvent.detail.gaEventCategory = 'tag_click';
        transformedEvent.detail.gaEventAction = event.detail.tagClicked;
        transformedEvent.detail.gaEventLabel = event.detail.tagSearched;
        break;

      case events.sponsored_search.position:
        transformedEvent.detail.gaEventCategory = 'sstk_position';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        transformedEvent.detail.gaEventLabel = event.detail.position;
        break;

      case events.sponsored_search.without_results:
        transformedEvent.detail.gaEventCategory = 'sponsored_search_without_results';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        break;

      case events.sstk.pagination_popup:
        transformedEvent.detail.gaEventCategory = 'sstk_popup';
        transformedEvent.detail.gaEventAction = 'pagination';
        break;

      case events.sstk.results_click:
        transformedEvent.detail.gaEventCategory = 'out_shutterstock';
        transformedEvent.detail.gaEventAction = 'click';
        break;

      case events.sstk.search_popup:
        transformedEvent.detail.gaEventCategory = 'sstk_popup';
        transformedEvent.detail.gaEventAction = event.detail.subId1;
        break;

      case events.checkout.error:
        transformedEvent.detail.gaEventCategory = 'ecommerce';
        transformedEvent.detail.gaEventAction = 'error';
        transformedEvent.detail.gaEventValue = event.detail.message;
        break;

      default:
        /* Do nothing */
    }

    this.trackEvent(event);
  }

  trackPageView = (url) => {
    this.ga('set', 'location', url);
    this.ga('send', 'pageview', this.pathFromUrl(url));
  }

  trackNoIndex() {
    if (this.searchType && this.searchPage > 1) {
      return;
    }

    const meta = document.querySelector('meta[name=ROBOTS]');
    if (meta) {
      const metaArray = meta.getAttribute('content').split(',');
      metaArray.map(item => item.trim());

      if (metaArray.includes('NOINDEX')) {
        this.ga('send', {
          hitType: 'event',
          eventCategory: 'noindex',
          eventAction: 'noindex',
          eventLabel: window.location.pathname,
          nonInteraction: true,
        });
      }
    }
  }

  trackEvent = (event) => {
    const fieldsObject = {
      hitType: 'event',
      nonInteraction: true,
    };

    /* refactor with Object.assign */
    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventCategory')) {
      fieldsObject.eventCategory = event.detail.gaEventCategory;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventAction')) {
      fieldsObject.eventAction = event.detail.gaEventAction;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventLabel')) {
      fieldsObject.eventLabel = event.detail.gaEventLabel;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventValue')) {
      fieldsObject.eventValue = event.detail.gaEventValue;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaNonInteraction')) {
      fieldsObject.nonInteraction = event.detail.gaNonInteraction;
    }

    this.ga('send', fieldsObject);
  }

  /* eslint-disable */
  // See https://developers.google.com/analytics/devguides/collection/analyticsjs/ for "inspiration"
  loadGA = function (i, s, o, g, r, a, m) {
    i['GoogleAnalyticsObject'] = r;
    i[r] = i[r] || function () {
      (i[r].q = i[r].q || []).push(arguments);
    }, i[r].l = 1 * new Date();
    a = s.createElement(o),
    m = s.getElementsByTagName(o)[0];
    a.async = 1;
    a.src = g;
    m.parentNode.insertBefore(a, m);
  };
  /* eslint-enable */

  ga(...args) {
    if (args[0] === 'send' && args[1] === 'event') {
      args.push({ nonInteraction: true });
    }

    window.ga(...args);
  }

  get scriptUrl() {
    return 'https://www.google-analytics.com/analytics.js';
  }

  get site() {
    return this.element.getAttribute('data-site');
  }

  get trackingId() {
    return this.element.getAttribute('data-tracking-id');
  }

  get userDimension() {
    return this.element.getAttribute('data-user-dimension');
  }

  transformAnalyticsProToPremium(eventProp) {
    if (eventProp === 'pro') {
      return 'premium';
    }

    return eventProp;
  }

  transformAnalyticsLicenseType(eventProp) {
    if (eventProp === 'pro_license') {
      return 'premium_license';
    }

    return eventProp;
  }

  transformAnalyticsSearchType(type) {
    const types = {
      internal: 'Internal',
      external: 'External',
    };

    if (types[type]) {
      return types[type];
    }

    console.error('unknown searchType: %s', type); /* eslint-disable-line  no-console */
    return null;
  }

  transformAnalyticsSearchFilterAction(filter) {
    const actions = {
      editor: 'filter_by_editor_compatible',
      'license-editorial': 'filter_by_license_type_editorial_use_only',
      'license-free': 'filter_by_license_type_standard',
      'license-pro': 'filter_by_license_type_premium',
      'orientation-horizontal': 'filter_by_orientation_horizontal',
      'orientation-square': 'filter_by_orientation_square',
      'orientation-vertical': 'filter_by_orientation_vertical',
      'sort-best': 'sort_by_best_match',
      'sort-newest': 'sort_by_newest',
    };

    return actions[filter];
  }

  pathFromUrl(pageUrl) {
    const url = new URL(pageUrl);

    if (this.searchForm) {
      url.searchParams.set('site-search', this.searchTerm);
      url.searchParams.set('search-scope', this.transformAnalyticsSearchType(this.searchType));
    }

    return `${url.pathname}${url.search}`;
  }

  get searchForm() {
    const form = document.querySelector('[data-controller~="search-form"]');
    if (form && form.getAttribute('data-search-term')) {
      return form;
    }

    return null;
  }

  get searchPage() {
    const currentPageElement = document.querySelector('[data-target="ez-num-pager.currentPage"]');
    let currentPage;
    if (currentPageElement) {
      currentPage = parseInt(currentPageElement.innerHTML, 10);
    }

    if (Number.isNaN(currentPage)) {
      return 1;
    }

    return currentPage;
  }

  get searchTerm() {
    if (this.searchForm) {
      return this.searchForm.getAttribute('data-search-term');
    }
    return null;
  }

  get searchType() {
    if (this.searchForm) {
      return this.searchForm.getAttribute('data-search-type');
    }
    return null;
  }

  get isTurbolinksPreview() {
    return document.documentElement.hasAttribute('data-turbolinks-preview');
  }
}
